import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { brandAlpha } from '../../../theme/mixins';

/**
 * The page shell, deliberately the same shape as the clinic dashboard's.
 *
 * These are duplicated from `clinicDashboard.styles.ts` rather than shared, which is the
 * convention in this tree: every feature owns its own page chrome, and only the pieces
 * that draw a *figure* -- the KPI tile, the chart furniture -- live in
 * `components/dashboard/`. That is the line worth holding: two pages may reasonably drift
 * in how their header sits, and must never drift in how they write an amount.
 */

export const headerBoxStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: 'space-between',
    gap: 2,
    mb: 3,
};

export const titleStyles: SxProps<Theme> = {
    fontWeight: 700,
    letterSpacing: '-0.015em',
};

export const subtitleStyles: SxProps<Theme> = {
    color: 'text.secondary',
};

/**
 * One control here rather than the clinic page's two: there is no Billed/Earned toggle,
 * because the whole point of this page is that both are on screen at once.
 */
export const controlsBoxStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    mb: 3,
};

export const controlGroupStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1,
};

export const controlLabelStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontWeight: 600,
    mr: 0.5,
};

export const toggleGroupStyles: SxProps<Theme> = {
    flexWrap: 'wrap',
};

/** The resolved dates, so the year is never the only statement of what is being shown. */
export const rangeCaptionStyles: SxProps<Theme> = {
    color: 'text.secondary',
    display: 'block',
    width: '100%',
};

/**
 * The rate chip, under the resolved dates rather than beside the year.
 *
 * `width: '100%'` on the caption above it already breaks the line, so this sits on its own
 * row -- which is what stops "Your share: 45%" reading as a caption on the year toggle. It is
 * a statement about every figure below it.
 */
export const shareChipStyles: SxProps<Theme> = {
    borderColor: brandAlpha('primary', 0.4),
    color: 'text.secondary',
    fontWeight: 600,
};

export const alertStyles: SxProps<Theme> = {
    mb: 3,
    borderRadius: 3,
};

export const loadingBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 8,
};

export const emptyStateBoxStyles: SxProps<Theme> = {
    textAlign: 'center',
    py: 8,
};

export const emptyStateIconStyles = (theme: Theme): SxProps<Theme> => ({
    fontSize: 64,
    color: theme.palette.grey[300],
    mb: 2,
});
